import CanvasClipArea from "ui/canvas/CanvasClipArea";
import Template from "ui/Template";
import PageElement from "ui/PageElement";
import {IS_HIGH_DPR, SCENE_FINAL_HEIGHT, SCENE_FINAL_WIDTH, SCENE_FPS, SCENE_FRAMES, WALL_HEIGHT_COEFFICIENT} from "ui/config";
import Utils from "ui/helpers/Utils";
import previewView from './html/preview.handlebars';

/**
 * Scene Preview Element
 */
export default class ScenePreview extends PageElement {
	/**
	 * @param {SceneSprites} sprites
	 * @param {SceneNeighbors} [neighbors]
	 * @param {number} [scale]
	 */
	constructor(sprites, neighbors = null, scale = 1) {
		super({parent: document.body});

		/**
		 * @type {number}
		 * @private
		 */
		this._scale = scale;

		/**
		 * @type {number}
		 * @private
		 */
		this._dprFactor = IS_HIGH_DPR ? 2 : 1;

		/**
		 * @type {SceneSprites}
		 * @private
		 */
		this._sprites = sprites;

		/**
		 * @type {SceneNeighbors}
		 * @private
		 */
		this._neighbors = neighbors;

		/**
		 * @type {T_CanvasElement}
		 * @private
		 */
		this._canvas = null;

		this._handlers = {
			keyDown: this._onKeyDown.bind(this),
			requestNextImage: this._requestNextImage.bind(this),
			onRangeChanged: this._onRangeChanged.bind(this),
			onButtonClick: this._onButtonClick.bind(this),
			stop: this._stop.bind(this)
		};

		/**
		 * @type {{
		 *  bgImage: HTMLCanvasElement|null,
		 *  rangeEl: HTMLInputElement,
		 *  buttons: Map<string,HTMLElement|Node>
		 * }}
		 * @private
		 */
		this._els = {
			buttons: new Map(),
			bgImage: null,
			rangeEl: null,
		};

		/**
		 * @type {{
		 *  isPlaying: boolean,
		 *  isButtonPressed: boolean,
		 *  frame: number,
		 *  frameTime: number,
		 *  timerId: number,
		 *  isShown: boolean,
		 *  floorHalfHeight: number,
		 * }}
		 * @private
		 */
		this._props = {
			isPlaying: true,
			isButtonPressed: false,
			frame: 0,
			frameTime: Math.ceil(1000 / SCENE_FPS),
			timerId: 0,
			isShown: false,
			floorHalfHeight: Math.round(
				(SCENE_FINAL_HEIGHT - Math.round(SCENE_FINAL_HEIGHT * WALL_HEIGHT_COEFFICIENT)) / 2
			)
		};

		this._build();
	}

	/**
	 * Builds preview element
	 *
	 * @private
	 * @returns {void}
	 */
	_build() {
		let props = this._props, els = this._els;

		if (this._els.bgImage === null && this._scale === 1) {
			const w = SCENE_FINAL_WIDTH * this._scale * this._dprFactor;
			const h = SCENE_FINAL_HEIGHT * this._scale * this._dprFactor;
			const c = Utils.createCanvas('', w, h);
			const clip = new CanvasClipArea(w, h);

			clip.createFloorPath(c.ctx, this._scale);
			c.ctx.closePath();
			c.ctx.fillStyle = '#fff';
			c.ctx.fill();
			this._els.bgImage = c.el;
		}

		let elem = new Template(previewView).createElement({});
		this._element = elem;

		if (props.timerId > 0) {
			clearTimeout(props.timerId);
		}
		els.rangeEl = elem.querySelector('.scene-preview__progress input[type="range"]');
		els.rangeEl.addEventListener('mousedown', this._handlers.stop);
		els.rangeEl.addEventListener('change', this._handlers.onRangeChanged);
		elem.querySelectorAll('.scene-preview__button[data-role]').forEach((el) => {
			els.buttons.set(el.getAttribute('data-role'), el);
			el.addEventListener('click', this._handlers.onButtonClick);
		});
		props.frame = 0;
		let el = elem.querySelector('canvas');
		this._canvas = {
			el,
			ctx: el.getContext('2d')
		};
		el.width = (SCENE_FINAL_WIDTH * 2) * this._dprFactor;
		el.height = (SCENE_FINAL_HEIGHT * 2) * this._dprFactor;
		el.style.width = `${SCENE_FINAL_WIDTH * 2}px`;
		el.style.height = `${SCENE_FINAL_HEIGHT * 2}px`;
		document.addEventListener('keydown', this._handlers.keyDown);
	}

	/**
	 * Resets animation
	 *
	 * @returns {ScenePreview}
	 */
	resume() {
		if (this._props.isShown) {
			void this._requestFrame(0);
		}
		if (!this._props.isButtonPressed) {
			this._emulateButtonClick('resume');
		}
		this.togglePlaying(true);
		return this;
	}

	/**
	 * Shows scene preview element
	 *
	 * @returns {ScenePreview}
	 */
	show() {
		let props = this._props;
		if (props.isShown) {
			return this;
		}
		props.isShown = true;
		props.isPlaying = true;
		this.activatePageElement();
		this._handlers.requestNextImage();
		return this;
	}

	/**
	 * Process Input Range change
	 *
	 * @private
	 * @returns {void}
	 */
	_onRangeChanged() {
		void this._requestFrame(parseInt(this._els.rangeEl.value));
	}

	/**
	 * Handles click bu panel button
	 *
	 * @param {MouseEvent} e
	 * @returns {void}
	 * @private
	 */
	_onButtonClick(e) {
		let el = e.target.closest('.scene-preview__button');
		this._props.isButtonPressed = true;
		switch (el.getAttribute('data-role')) {
			case 'play':
				this.togglePlaying();
				break;
			case 'resume':
				this.resume();
				break;
			case 'close':
				this.destroy();
				break;
		}
		this._props.isButtonPressed = false;
	}

	/**
	 * Plays/pauses animation
	 *
	 * @param {?boolean} [flag]
	 * @returns {ScenePreview}
	 */
	togglePlaying(flag = null) {
		let props = this._props;
		if (flag === false || (flag === null && props.isPlaying)) {
			if (this._element) {
				this._element.classList.remove('scene-preview_playing');
			}
			props.isPlaying = false;
			this._stop();
		} else if (flag === true || (flag === null && !props.isPlaying)) {
			if (this._element) {
				this._element.classList.add('scene-preview_playing');
			}
			props.isPlaying = true;
			this._requestNextImage();
		}
		if (!props.isButtonPressed && flag === null) {
			this._emulateButtonClick('play');
		}
		return this;
	}

	/**
	 * Emulates click by button
	 *
	 * @param {string} role
	 * @returns {void}
	 * @private
	 */
	_emulateButtonClick(role) {
		let but = this._els.buttons.get(role);
		if (but) {
			but.classList.add('scene-preview__button_pressed');
			setTimeout(() => {
				but.classList.remove('scene-preview__button_pressed');
			}, 100);
		}
	}

	/**
	 * Stops animation
	 *
	 * @private
	 * @returns {void}
	 */
	_stop() {
		if (this._props.timerId > 0) {
			clearTimeout(this._props.timerId);
		}
	}

	/**
	 * Starts animation
	 *
	 * @private
	 * @param {number} [lastTime]
	 * @returns {void}
	 */
	_start(lastTime = 0) {
		if (!this._props.isPlaying) {
			return;
		}
		this._props.timerId = setTimeout(
			this._handlers.requestNextImage,
			Math.max(20, this._props.frameTime - (lastTime > 0 ? Date.now() - lastTime : 0))
		);
	}

	/**
	 * Returns animation frame
	 *
	 * @param {number} frame
	 * @returns {void}
	 * @private
	 */
	async _requestFrame(frame) {
		let props = this._props;
		this._stop();
		let time = Date.now();
		props.frame = frame;
		this._els.rangeEl.value = String(props.frame);
		this.render();
		this._start(time);
	}

	/**
	 * Renders current frame
	 *
	 * @returns {void}
	 */
	render() {
		const el = this._canvas.el;
		const ctx = this._canvas.ctx;
		const frame = this._props.frame;

		let offsetX = 0;
		let offsetY = 0;

		if (this._scale > 1) {
			ctx.save();
			ctx.fillStyle = '#ffffff';
			ctx.fillRect(0, 0, el.width, el.height);
			ctx.restore();
		} else {
			ctx.clearRect(0, 0, el.width, el.height);
		}

		ctx.imageSmoothingEnabled = false;
		ctx.webkitImageSmoothingEnabled = false;
		ctx.mozImageSmoothingEnabled = false;

		offsetX = ((el.width / this._dprFactor) >> 1) - ((SCENE_FINAL_WIDTH >> 1) * this._scale);
		offsetY = ((el.height / this._dprFactor) >> 1) - ((SCENE_FINAL_HEIGHT >> 1) * this._scale);

		if (this._neighbors !== null) {
			ctx.save();
			this._neighbors.drawScenes(
				1, ctx, frame, SCENE_FINAL_WIDTH, SCENE_FINAL_HEIGHT, -offsetX, -offsetY, this._scale * this._dprFactor
			);
			ctx.restore();
		}

		if (this._els.bgImage) {
			ctx.drawImage(this._els.bgImage, offsetX * this._dprFactor, offsetY * this._dprFactor);
		}

		ctx.save();
		this._sprites.drawFrame(this._canvas.ctx, frame, offsetX * this._dprFactor, offsetY * this._dprFactor, this._scale * this._dprFactor);
		ctx.restore();

		if (this._neighbors !== null) {
			ctx.save();
			this._neighbors.drawScenes(
				2, ctx, frame, SCENE_FINAL_WIDTH, SCENE_FINAL_HEIGHT, -offsetX, -offsetY, this._scale * this._dprFactor
			);
			ctx.restore();
		}

	}

	/**
	 * Requests to render next frame
	 *
	 * @private
	 * @returns {void}
	 */
	_requestNextImage() {
		void this._requestFrame((this._props.frame + 1) % SCENE_FRAMES);
	}

	/**
	 * Processes key down event for preview dialog box
	 *
	 * @param {KeyboardEvent} e
	 * @returns {void}
	 * @private
	 */
	_onKeyDown(e) {
		if (!this._isSelfFocus()) {
			// the editor not in focus
			return;
		}
		switch (e.code) {
			case 'Esc':
			case 'Escape':
				this.destroy();
				break;
			case 'F9':
				this.resume();
				break;
			case 'Space':
				this.togglePlaying();
				break;
		}
	}

	/**
	 * @inheritDoc
	 * @returns {void}
	 */
	destroy() {
		super.destroy();
		let props = this._props, els = this._els;
		if (!props.isShown) {
			return;
		}
		this._stop();
		document.removeEventListener('keydown', this._handlers.keyDown);
		props.isPlaying = false;
		props.isButtonPressed = false;
		props.isShown = false;
		els.buttons.clear();
		this._canvas = null;
		this._sprites = null;
	}
}
